/**
 * SyncDictionariesModal — synchronizacja SŁOWNIKÓW z Symfonia FK (pull-only, dane referencyjne).
 *
 * Spójny z importem/eksportem kontrahentów/pracowników: potwierdzenie → progress → wspólny widok
 * wyniku `SyncResultPanel`. Słowniki to dane referencyjne (nie rekordy kartotek) → pigułki są
 * LICZNIKAMI bez drill-downu (panel sam ukrywa obszar listy, gdy nic nie ma rekordów).
 *
 * Endpoint `POST /erp-integrations/:id/sync-dictionaries` (direct: leci inline, zwraca jobId).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { AlertCircle, CheckCircle2, Clock, Loader2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

import { isJobInFlight } from './job-in-flight';
import { type SyncRecordDetail, SyncResultPanel, type SyncResultTile } from './SyncResultPanel';
import type { JobDto } from './TestConnectionModal';

interface Props {
  open: boolean;
  integrationId: string | null;
  integrationName: string;
  onClose: () => void;
}

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 3 * 60_000;

interface DictStats {
  dictionaries_synced?: number;
  items_added?: number;
  items_updated?: number;
  items_deactivated?: number;
  skipped_mappings?: number;
}

interface DictResultPayload {
  outcome?: 'ok' | 'partial' | 'no_op';
  stats?: DictStats;
  warnings?: string[];
  /** Lista słowników do drill-downu (klik pigułki „Słowniki"). */
  details?: { dictionaries?: SyncRecordDetail[] };
}

const PREFIX = 'tenant.settings.integrations.syncDictionaries';
const TILE_PREFIX = 'tenant.settings.integrations.syncHistory.resultTiles';

export function SyncDictionariesModal({ open, integrationId, integrationName, onClose }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<'confirm' | 'progress'>('confirm');
  const [expanded, setExpanded] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!open) {
      setStage('confirm');
      setExpanded(false);
      setJobId(null);
      setTimedOut(false);
      return;
    }
    if (stage !== 'progress' || !jobId) return;
    const timer = window.setTimeout(() => setTimedOut(true), POLL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [open, stage, jobId]);

  const startMutation = useMutation({
    mutationFn: async () => {
      if (!integrationId) throw new Error('No integrationId');
      const res = await api.post<{ jobId: string }>(
        `/erp-integrations/${integrationId}/sync-dictionaries`,
      );
      return res.data.jobId;
    },
    onSuccess: (id) => {
      setJobId(id);
      setStage('progress');
    },
    onError: (err) => {
      const code = isAxiosError(err) ? (err.response?.data?.error as string | undefined) : null;
      if (code === 'AGENT_TOKEN_NOT_GENERATED') {
        toast.show({ variant: 'warning', title: t(`${PREFIX}.noAgentToken`) });
        return;
      }
      if (code === 'INTEGRATION_DISABLED') {
        toast.show({ variant: 'warning', title: t(`${PREFIX}.integrationDisabled`) });
        return;
      }
      toast.show({ variant: 'error', title: t(`${PREFIX}.startError`) });
    },
  });

  const jobQ = useQuery({
    queryKey: ['erp-sync-job', jobId],
    queryFn: async () => {
      const res = await api.get<{ job: JobDto }>(`/erp-sync-jobs/${jobId}`);
      return res.data.job;
    },
    enabled: !!jobId && open && !timedOut && stage === 'progress',
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;
      return POLL_INTERVAL_MS;
    },
  });

  useEffect(() => {
    if (jobQ.data?.status === 'completed' || jobQ.data?.status === 'failed') {
      void queryClient.invalidateQueries({ queryKey: ['erp-integrations'] });
    }
  }, [jobQ.data?.status, queryClient]);

  if (!open) return null;

  const job = jobQ.data ?? null;
  const result = (job?.result ?? null) as DictResultPayload | null;
  const s = result?.stats ?? {};
  const dictionaryList = result?.details?.dictionaries ?? [];
  // Pigułka „Słowniki" ma drill-down = lista słowników (nazwa + klucz FK + liczby pozycji);
  // pozostałe pigułki to same liczniki (bez rekordów).
  const resultTiles: SyncResultTile[] = [
    {
      key: 'dictSynced',
      label: t(`${TILE_PREFIX}.dictSynced`),
      count: s.dictionaries_synced ?? 0,
      tone: 'neutral',
      records: dictionaryList,
    },
    {
      key: 'dictItemsAdded',
      label: t(`${TILE_PREFIX}.dictItemsAdded`),
      count: s.items_added ?? 0,
      tone: 'added',
    },
    {
      key: 'dictItemsUpdated',
      label: t(`${TILE_PREFIX}.dictItemsUpdated`),
      count: s.items_updated ?? 0,
      tone: 'updated',
    },
    {
      key: 'dictItemsDeactivated',
      label: t(`${TILE_PREFIX}.dictItemsDeactivated`),
      count: s.items_deactivated ?? 0,
      tone: 'muted',
    },
    {
      key: 'dictSkippedMappings',
      label: t(`${TILE_PREFIX}.dictSkippedMappings`),
      count: s.skipped_mappings ?? 0,
      tone: 'warning',
    },
  ];

  // Wysyłanie żądania albo zadanie w toku — tło i Esc nie zamykają okna (X i „Zamknij" działają).
  const running =
    startMutation.isPending || (stage === 'progress' && isJobInFlight(job, timedOut, jobQ.isError));

  return (
    <Modal
      open
      onClose={onClose}
      title={t(`${PREFIX}.title`, { name: integrationName })}
      size="lg"
      expandable
      expanded={expanded}
      onExpandedChange={setExpanded}
      testId="sync-dictionaries-modal"
      closeOnBackdrop={!running}
      closeOnEsc={!running}
      footer={
        <>
          {stage === 'confirm' && (
            <>
              <Button variant="secondary" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => startMutation.mutate()}
                disabled={startMutation.isPending}
                data-testid="sync-dictionaries-start-btn"
              >
                {startMutation.isPending ? t(`${PREFIX}.starting`) : t(`${PREFIX}.startButton`)}
              </Button>
            </>
          )}
          {stage === 'progress' && (
            <Button variant="primary" onClick={onClose} data-testid="sync-dictionaries-close-btn">
              {t('common.close')}
            </Button>
          )}
        </>
      }
    >
      {stage === 'confirm' && (
        <div className="space-y-4" data-testid="sync-dictionaries-stage-confirm">
          <p className="text-sm text-text-secondary">{t(`${PREFIX}.confirmHint`)}</p>
          {startMutation.isError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-400">
              {t(`${PREFIX}.startError`)}
            </div>
          )}
        </div>
      )}

      {stage === 'progress' && (
        <div
          className="flex h-full min-h-0 flex-col gap-4"
          data-testid={`sync-dictionaries-status-${
            job?.status === 'completed' || job?.status === 'failed'
              ? job.status
              : timedOut
                ? 'timeout'
                : (job?.status ?? 'running')
          }`}
        >
          {!timedOut && (!job || job.status === 'pending' || job.status === 'running') && (
            <div className="flex items-center gap-3 rounded-md bg-bg-secondary p-3">
              <Loader2 className="h-5 w-5 animate-spin text-accent-500" aria-hidden="true" />
              <div>
                <div className="font-medium text-text-primary">
                  {job?.status === 'running'
                    ? t(`${PREFIX}.running`)
                    : t(`${PREFIX}.waitingForAgent`)}
                </div>
                <div className="text-xs text-text-tertiary">{t(`${PREFIX}.runningHint`)}</div>
              </div>
            </div>
          )}

          {timedOut && job?.status !== 'completed' && job?.status !== 'failed' && (
            <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3">
              <Clock className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400" aria-hidden="true" />
              <div>
                <div className="font-medium text-red-600 dark:text-red-400">
                  {t(`${PREFIX}.timeoutTitle`)}
                </div>
                <div className="text-xs text-text-secondary">{t(`${PREFIX}.timeoutHint`)}</div>
              </div>
            </div>
          )}

          {job?.status === 'completed' && (
            <div className="flex min-h-0 flex-1 flex-col gap-3">
              <div className="flex shrink-0 items-start gap-3 rounded-md border border-green-500/30 bg-green-500/10 p-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600" aria-hidden="true" />
                <div>
                  <div className="font-medium text-green-700 dark:text-green-400">
                    {t(`${PREFIX}.successTitle`)}
                  </div>
                  <div className="text-xs text-text-secondary">{t(`${PREFIX}.successHint`)}</div>
                </div>
              </div>

              <SyncResultPanel
                tiles={resultTiles}
                testId="sync-dictionaries-result"
                fillHeight={expanded}
                listHeightClass="h-72"
              />
            </div>
          )}

          {job?.status === 'failed' && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3">
                <AlertCircle
                  className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400"
                  aria-hidden="true"
                />
                <div>
                  <div className="font-medium text-red-600 dark:text-red-400">
                    {t(`${PREFIX}.failedTitle`)}
                  </div>
                  <div className="text-xs text-text-secondary">{t(`${PREFIX}.failedHint`)}</div>
                </div>
              </div>
              {job.result && (
                <pre className="max-h-64 overflow-auto rounded-md border border-red-500/30 bg-red-500/5 p-3 font-mono text-xs text-red-700 dark:text-red-400">
                  {JSON.stringify(job.result, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
